/* Backdrop Contest — the weekly community screenshot competition. Citizens submit
   their best city shot (AI-scanned before it goes live), everyone votes once a
   week, and the studio crowns the winner as the main menu's backdrop. */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Camera, Crown, ThumbsUp, Loader2, Upload, ImageIcon } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import SiteNav from "@/components/SiteNav";

export default function BackdropPage() {
  const [wall, setWall] = useState(null);
  const [current, setCurrent] = useState(null);
  const [me, setMe] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    api.get("/nc/backdrop/wall").then((r) => setWall(r.data)).catch(() => setWall({ entries: [] }));
    api.get("/nc/backdrop/current").then((r) => setCurrent(r.data.backdrop)).catch(() => {});
    if (localStorage.getItem("nc_token")) api.get("/auth/me").then((r) => setMe(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async (file) => {
    if (!file || busy) return;
    if (!me) { toast.error("Sign in to enter the contest"); return; }
    setBusy("submit");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/nc/backdrop/submit", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(r.data.message);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const vote = async (id) => {
    if (!me) { toast.error("Sign in to vote"); return; }
    if (busy) return;
    setBusy(id);
    try {
      const r = await api.post(`/nc/backdrop/${id}/vote`);
      toast.success(r.data.message);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const pick = async (id) => {
    if (busy) return;
    setBusy(`pick-${id}`);
    try {
      const r = await api.post("/admin/nc/backdrop/pick", { entry_id: id });
      toast.success(r.data.message);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const isStaff = me && ["admin", "dev"].includes(me.role);

  return (
    <div data-testid="backdrop-page" className="min-h-screen bg-[#05070a] text-zinc-100">
      <SiteNav />
      <div className="max-w-6xl mx-auto px-6 py-14">
        <p className="font-mono text-[10px] tracking-[0.4em] uppercase text-nc-orange flex items-center gap-2">
          <Camera className="w-4 h-4" /> Weekly contest · {wall?.week}
        </p>
        <h1 className="font-bold uppercase tracking-wide text-2xl sm:text-4xl mt-3 leading-tight">The Backdrop Contest</h1>
        <p className="font-mono text-xs text-zinc-400 mt-4 max-w-2xl leading-relaxed">
          Your best shot of the city becomes the main menu every player boots into. One vote per citizen per week —
          the studio crowns the winner and it goes live across every world.
        </p>

        {/* reigning backdrop */}
        {current?.url && (
          <div data-testid="backdrop-current" className="mt-10 border border-nc-orange/30 bg-black/40 p-4">
            <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-nc-orange flex items-center gap-2 mb-3">
              <Crown className="w-3.5 h-3.5" /> Reigning backdrop — shot by {current.by}
            </p>
            <img src={current.url} alt="Current menu backdrop" className="w-full max-h-[320px] object-cover border border-white/10" />
          </div>
        )}

        {/* submit */}
        <div className="mt-8 border border-dashed border-white/20 bg-white/[0.02] p-6 flex items-center gap-4 flex-wrap">
          <Upload className="w-5 h-5 text-zinc-500 shrink-0" />
          <div className="flex-1 min-w-60">
            <p className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-200">Enter this week's contest</p>
            <p className="font-mono text-[9px] text-zinc-500 mt-1">PNG/JPEG/WEBP up to 8MB · every entry is AI-scanned before it goes live · 3 entries per day</p>
          </div>
          <label data-testid="backdrop-submit" className={`font-mono text-[9px] uppercase tracking-[0.2em] px-5 py-3 font-bold cursor-pointer transition-colors ${me ? "bg-nc-orange text-black hover:bg-nc-orange/80" : "border border-white/15 text-zinc-500"}`}>
            {busy === "submit" ? <Loader2 className="w-4 h-4 animate-spin" /> : (me ? "Submit a shot" : "Sign in to enter")}
            {me && <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => submit(e.target.files?.[0])} />}
          </label>
        </div>

        {/* the wall */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-10">
          {(wall?.entries || []).map((e) => (
            <div key={e.id} data-testid={`backdrop-entry-${e.id}`} className="border border-white/10 bg-black/40 overflow-hidden group">
              <div className="aspect-video bg-black/60 overflow-hidden">
                <img src={e.url} alt={`Backdrop entry by ${e.author}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
              </div>
              <div className="flex items-center gap-2 px-3.5 py-2.5">
                <p className="font-mono text-[9px] text-zinc-300 truncate flex-1">{e.author}</p>
                <button data-testid={`backdrop-vote-${e.id}`} onClick={() => vote(e.id)} disabled={!!busy}
                  className="font-mono text-[9px] uppercase tracking-[0.1em] border border-white/15 hover:border-nc-orange/60 hover:text-nc-orange px-2.5 py-1.5 transition-colors flex items-center gap-1">
                  <ThumbsUp className="w-3 h-3" /> {e.votes}
                </button>
                {isStaff && (
                  <button data-testid={`backdrop-pick-${e.id}`} onClick={() => pick(e.id)} disabled={!!busy}
                    className="font-mono text-[9px] uppercase tracking-[0.1em] border border-nc-orange/50 text-nc-orange hover:bg-nc-orange/10 px-2.5 py-1.5 transition-colors flex items-center gap-1">
                    <Crown className="w-3 h-3" /> Crown
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
        {wall && wall.entries.length === 0 && (
          <div className="border border-dashed border-white/15 p-12 text-center mt-10">
            <ImageIcon className="w-8 h-8 text-zinc-600 mx-auto" />
            <p className="font-mono text-xs text-zinc-400 mt-4">No entries this week yet — the first submission sets the bar.</p>
          </div>
        )}
      </div>
    </div>
  );
}
